import { useBetterFeatureClient } from "@di-devs/utils-fe";
import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import type { FeatureClient } from "../../shared/types";
import {
	useAudiences,
	useCreateAudience,
	useDeleteAudience,
	useSearchAudience,
} from "../hooks/api.hooks";
import type { BaseAudience, IApiClient } from "../interfaces/client.interface";
import type {
	AudienceContextValue,
	AudienceTabDefinition,
	ModalState,
	PublicSettings,
	UseAudienceParams,
} from "../types";

const AudienceContext = createContext<AudienceContextValue<any> | undefined>(
	undefined,
);

export const useAudienceContext = <
	TAudience extends BaseAudience = BaseAudience,
>(
	params?: UseAudienceParams,
): AudienceContextValue<TAudience> => {
	const context = useContext(AudienceContext);
	if (!context) {
		throw new Error("useAudience must be used within AudienceProvider");
	}

	const activeQuery = useAudiences(params);

	return useMemo(
		() => ({
			...(context as AudienceContextValue<TAudience>),
			hooks: {
				...(context as AudienceContextValue<TAudience>).hooks,
				audiencesQuery: activeQuery as any,
			},
		}),
		[context, activeQuery],
	);
};

export interface AudienceProviderProps<
	TClient extends IApiClient = IApiClient,
> {
	children: ReactNode;
	publicSettings?: PublicSettings;
	apiClient?: TClient;
	defaultTabIndex?: number;
}

export const AudienceProvider = <
	TAudience extends BaseAudience = BaseAudience,
	TClient extends IApiClient = IApiClient,
>({
	children,
	publicSettings,
	apiClient,
	defaultTabIndex = 0,
}: AudienceProviderProps<TClient>) => {
	const [activeTabIndex, setActiveTabIndex] = useState<number>(defaultTabIndex);
	const [tabs, setTabs] = useState<AudienceTabDefinition[]>([]);
	const [modalState, setModalState] = useState<ModalState<TAudience>>({
		visible: false,
		type: null,
		data: null,
	});
	const queryClient = useQueryClient();
	const betterFeatureClient = useBetterFeatureClient<FeatureClient>();

	// Use provided API client or create default from Better Feature client
	const client = apiClient || betterFeatureClient;

	// Better Feature hooks (always available)
	const audiencesQuery = useAudiences();
	const createAudienceMutation = useCreateAudience();
	const deleteAudienceMutation = useDeleteAudience();
	const searchAudienceMutation = useSearchAudience();

	// Tab registration function
	const registerTabs = useCallback(
		(newTabs: AudienceTabDefinition[]) => {
			setTabs(newTabs);
			// If this is the first registration and we don't have an active tab, set to first tab
			if (newTabs.length > 0 && activeTabIndex >= newTabs.length) {
				setActiveTabIndex(0);
			}
		},
		[activeTabIndex],
	);

	// WebSocket event listeners setup would go here if needed
	// Real-time update handlers can be re-added here if WebSocket functionality is restored

	const openModal = useCallback(
		(type: ModalState<TAudience>["type"], data: TAudience | null = null) => {
			setModalState({ visible: true, type, data });
		},
		[],
	);

	const closeModal = useCallback(() => {
		setModalState({ visible: false, type: null, data: null });
	}, []);

	const handleTabChange = useCallback(
		(index: number) => {
			if (index >= 0 && index < tabs.length) {
				setActiveTabIndex(index);
				// Close modal if open
				closeModal();
			}
		},
		[tabs.length, closeModal],
	);

	const value: AudienceContextValue<TAudience> = useMemo(
		() => ({
			activeTabIndex,
			setActiveTabIndex: handleTabChange,
			tabs,
			registerTabs,
			modalState,
			openModal,
			closeModal,
			queryClient,
			apiClient: client as IApiClient,
			hooks: {
				audiencesQuery,
				createAudienceMutation,
				deleteAudienceMutation,
				searchAudienceMutation,
			},
			publicSettings,
		}),
		[
			activeTabIndex,
			handleTabChange,
			tabs,
			registerTabs,
			modalState,
			openModal,
			closeModal,
			queryClient,
			client,
			audiencesQuery,
			createAudienceMutation,
			deleteAudienceMutation,
			searchAudienceMutation,
			publicSettings,
		],
	);

	return (
		<AudienceContext.Provider value={value}>
			{children}
		</AudienceContext.Provider>
	);
};

// Custom hook for accessing API client from context
export const useAudienceApiClient = <
	TAudience extends BaseAudience = BaseAudience,
>() => {
	const { apiClient } = useAudienceContext<TAudience>();
	return apiClient;
};

// Custom hook for modal management
export const useAudienceModal = <
	TAudience extends BaseAudience = BaseAudience,
>() => {
	const { modalState, openModal, closeModal } = useAudienceContext<TAudience>();
	return {
		...modalState,
		openModal,
		closeModal,
		isOpen: modalState.visible,
	};
};
